<template>
  <BaseTooltip
    :content="tooltipText || label"
    :display-condition="displayTooltip"
    placement="right"
    style="min-width: 0"
  >
    <component
      :is="componentType"
      :id="`pendo-nav-link-${label.toLowerCase().replace(/\s+/g, '-')}`"
      :aria-disabled="disabled"
      class="client-side-nav__link cr-flex cr-align-center cr-justify-between"
      :class="{
        'client-side-nav__link--disabled': disabled || !permissible,
        'client-side-nav__link--expandable': expandable,
        'client-side-nav__link--not-permissible': !permissible,
        'active': active,
      }"
      :data-testid="`client-side-nav-link-${label.toLowerCase().replace(/\s+/g, '-')}`"
      :href="(link && !disabled && permissible) ? link : ''"
      :to="(useRouterLink && !disabled && permissible) ? {name: link} : ''"
      @click="handleClick"
      @mouseenter="hovering = true"
      @mouseleave="hovering = false"
    >
      <div class="client-side-nav__link__left cr-flex cr-align-center gap-2">
        <div class="client-side-nav__icon-wrapper cr-flex cr-align-center cr-justify-center">
          <BaseIcon
            v-if="icon"
            class="client-side-nav__icon"
            font-size="1rem"
            :icon="iconWithWeight"
          />
        </div>
        <div
          class="client-side-nav__link-text label-m-regular"
        >
          {{ label }}
        </div>
      </div>
      <div class="side-nav__link__right">
        <BaseIcon
          v-if="expandable"
          :class="{ expanded: expanded }"
          font-size="var(--font-size-1)"
          :icon="'fa-regular fa-chevron-down'"
        />
      </div>
      <slot name="decorations" />
    </component>
  </BaseTooltip>
</template>

<script>
import BaseIcon from './BaseIcon.vue'
import BaseTooltip from './BaseTooltip.vue'

export default {
  name: 'ClientSideNavMenuItem',
  components: {
    BaseIcon,
    BaseTooltip,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      required: false,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    link: {
      type: String,
      default: '',
    },
    permissible: {
      type: Boolean,
      default: true,
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
      default: '',
    },
    useRouterLink: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['expanded'],
  data() {
    return {
      hovering: false,
    }
  },
  computed: {
    componentType() {
      if (this.link && this.useRouterLink) {
        return 'router-link'
      } else if (this.link) {
        return 'a'
      } else {
        return 'button'
      }
    },
    increaseWeight() {
      return !this.disabled && this.permissible && (this.hovering || this.active)
    },
    iconWithWeight() {
      return this.increaseWeight ? `fa-solid ${this.icon}` : `fa-regular ${this.icon}`
    },
    displayTooltip() {
      return this.showTooltip && this.hovering
    },
  },
  methods: {
    handleClick() {
      if (this.expandable && !this.disabled && this.permissible) {
        this.$emit('expanded', this.id)
      }
    },
  },
}
</script>

<style scoped lang="scss">
button {
  background: none;
	color: inherit;
	border: none;
	padding: 0;
	font: inherit;
	cursor: pointer;
	outline: inherit;
}

.client-side-nav__link {
  width: 100%;
  padding: var(--spacing-2, .5rem) var(--spacing-3, .75rem);
  border-radius: var(--cr-br-small);
  cursor: pointer;
  color: var(--pepper-1000);
  text-decoration: none;

  &.active:not(&--disabled):not(&--not-permissible) {
    background-color: var(--blueberry-100);
    color: var(--blueberry-1000);
    .client-side-nav__link-text {
      font-weight: 500;
    };
  }

  &--disabled, &--not-permissible {
    cursor: not-allowed;
    color: var(--pepper-400);
  }

  &:hover:not(&--disabled):not(&--not-permissible):not(&--expandable):not(.router-link-active) {
    box-shadow: var(--cr-ds-medium);
  }

  &:hover:not(&--disabled):not(&--not-permissible) {
    .client-side-nav__link-text {
      font-weight: 500;
    }
  }
}

.client-side-nav__icon-wrapper {
  height: 20px;
  width: 20px;
}

.client-side-nav__link-text {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fa-chevron-down {
  transition: 300ms linear all;
  transform: rotate(0deg);
  &.expanded {
    transform: rotate(-90deg);
  }
}
</style>
